import { HttpStatusCode } from "axios";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { NotificationsApi } from "../api";
import { errors } from "../constants";
import {
  Log,
  shouldRedirectToLoginForCaughtApiException,
  showTokenExpiredAlert,
  storeCurrentPageAndRedirectToLogin,
} from "../helpers";
import { ApiException } from "../models";
import {
  notificationsCountAtom,
  notificationsImportantAtom,
  tableRowsAtomFamily,
} from "../recoil";

export const useNotificationsApi = () => {
  const { t } = useTranslation();
  const setNotificationsCount = useSetRecoilState(notificationsCountAtom);
  const setNotificationsImportant = useSetRecoilState(
    notificationsImportantAtom,
  );
  const setTableRows = useSetRecoilState(tableRowsAtomFamily("notification"));

  const fetchCount = useEvent(async () => {
    try {
      const receivedNotificationsCount = await NotificationsApi.getCount();

      setNotificationsCount(receivedNotificationsCount);
    } catch (error) {
      if (error instanceof ApiException) {
        if (shouldRedirectToLoginForCaughtApiException(error)) {
          showTokenExpiredAlert();
          storeCurrentPageAndRedirectToLogin();
          setNotificationsCount(0);
          return;
        }
        if (error.statusCode === HttpStatusCode.Unauthorized) {
          setNotificationsCount(0);
          return;
        }
      }

      Log.errorAndToast(t(errors.notification.errorGettingNotifications));
    }
  });
  const fetchImportant = useEvent(async () => {
    try {
      setNotificationsImportant(prev => ({ ...prev, isLoading: true }));
      const receivedImportantNotifications =
        await NotificationsApi.getImportant();

      if (!receivedImportantNotifications) {
        Log.errorAndToast(t(errors.notification.errorGettingNotifications));
        return;
      }

      const { notifications, countSummary } = receivedImportantNotifications;

      setTableRows(notifications);
      setNotificationsImportant({
        ids: new Set(notifications.map(({ id }) => id)),
        summary: countSummary,
        isLoading: false,
      });
    } catch (error) {
      if (shouldRedirectToLoginForCaughtApiException(error)) {
        showTokenExpiredAlert();
        storeCurrentPageAndRedirectToLogin();
        setNotificationsImportant(prev => ({ ...prev, isLoading: false }));
        return;
      }

      Log.errorAndToast(t(errors.notification.errorGettingNotifications));
      setNotificationsImportant(prev => ({ ...prev, isLoading: false }));
    }
  });

  return {
    fetchCount,
    fetchImportant,
  };
};
